<template>
  <div class="competition-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>{{ data.name }}</h1>
    </div>

    <!-- 数据统计部分 -->
    <div class="stats-sections">
      <!-- 实时完赛数 -->
      <van-card class="stats-card">
        <template #title>
          <div class="card-title">
            <van-icon name="completed" class="card-icon" />
            <span>实时完赛数</span>
            <span class="total-count"
              >总计: {{ formatNumber(data.realTimeCount) }}</span
            >
          </div>
        </template>
        <template #desc>
          <div class="stats-content">
            <div
              v-for="(count, distance) in data.realTimeDistanceCountMap"
              :key="'real-time-' + distance"
              class="distance-item"
            >
              <span class="distance">{{ formatDistance(distance) }}：</span>
              <span class="count">{{ formatNumber(count) }}人</span>
            </div>
          </div>

          <!-- 实时完赛数查询部分 -->
          <div class="query-section">
            <van-cell-group inset>
              <van-field
                v-model="realTimeQueryNumber"
                label="参赛号码"
                placeholder="请输入参赛号码"
                clearable
              />
            </van-cell-group>
            <div class="query-btn-container">
              <van-button
                type="primary"
                size="large"
                class="query-btn"
                @click="handleRealTimeQuery"
                :loading="realTimeLoading"
                style="width: 70%; height: 40px"
              >
                查询实时成绩
              </van-button>
            </div>
          </div>
        </template>
      </van-card>

      <!-- 赛后成绩推送数 -->
      <van-card class="stats-card">
        <template #title>
          <div class="card-title">
            <van-icon name="orders-o" class="card-icon" />
            <span>赛后成绩推送数</span>
            <span class="total-count"
              >总计: {{ formatNumber(data.afterGamePushCount) }}</span
            >
          </div>
        </template>
        <template #desc>
          <div class="stats-content">
            <div
              v-for="(count, distance) in data.afterGameDistanceCountMap"
              :key="'push-' + distance"
              class="distance-item"
            >
              <span class="distance">{{ formatDistance(distance) }}：</span>
              <span class="count">{{ formatNumber(count) }}人</span>
            </div>
          </div>

          <!-- 赛后成绩查询部分 -->
          <div class="query-section">
            <van-cell-group inset>
              <van-field
                v-model="afterGameQueryNumber"
                label="参赛号码"
                placeholder="请输入参赛号码"
                clearable
              />
            </van-cell-group>
            <div class="query-btn-container">
              <van-button
                type="primary"
                size="large"
                class="query-btn"
                @click="handleAfterGameQuery"
                :loading="afterGameLoading"
                style="width: 70%; height: 40px"
              >
                查询最终成绩
              </van-button>
            </div>
          </div>
        </template>
      </van-card>
    </div>

    <!-- 实时成绩弹出层 -->
    <van-popup
      v-model="showRealTimePopup"
      position="bottom"
      round
      :style="{ height: '85%', borderRadius: '20px 20px 0 0' }"
    >
      <div class="popup-header">
        <h2>实时成绩详情</h2>
        <van-icon
          name="cross"
          class="close-icon"
          @click="showRealTimePopup = false"
        />
      </div>
      <div class="popup-content">
        <div class="result-section">
          <div class="result-row">
            <span class="label">号码：</span>
            <span class="value">{{ realTimeQueryResult.fullBibno }}</span>
          </div>
          <div class="result-row">
            <span class="label">净成绩：</span>
            <span class="value">{{
              formatDuration(realTimeQueryResult.pureDuration)
            }}</span>
          </div>
          <div class="result-row">
            <span class="label">枪成绩：</span>
            <span class="value">{{
              formatDuration(realTimeQueryResult.gunDuration)
            }}</span>
          </div>
          <div class="result-row">
            <span class="label">平均配速：</span>
            <span class="value">{{
              formatPace(realTimeQueryResult.averagePace)
            }}</span>
          </div>
        </div>

        <van-collapse v-model="activeCollapse" accordion>
          <van-collapse-item title="净时分段成绩" name="1">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in realTimeQueryResult.purePartTimeDurationList"
                :key="'net-' + key"
                class="split-item"
              >
                <span class="split-distance">{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>
              </div>
            </div>
          </van-collapse-item>

          <van-collapse-item title="枪时分段成绩" name="2">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in realTimeQueryResult.gunPartTimeDurationList"
                :key="'gun-' + key"
                class="split-item"
              >
                <span class="split-distance">{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>
              </div>
            </div>
          </van-collapse-item>

          <van-collapse-item title="净时累计成绩" name="3">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in realTimeQueryResult.pureAddTimeDurationList"
                :key="'cum-' + key"
                class="split-item"
              >
                <span class="split-distance">{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>
              </div>
            </div>
          </van-collapse-item>
        </van-collapse>
      </div>
    </van-popup>

    <!-- 赛后成绩弹出层 -->
    <van-popup
      v-model="showAfterGamePopup"
      position="bottom"
      round
      :style="{ height: '90%', borderRadius: '20px 20px 0 0' }"
    >
      <div class="popup-header">
        <h2>赛后成绩详情</h2>
        <van-icon
          name="cross"
          class="close-icon"
          @click="showAfterGamePopup = false"
        />
      </div>
      <div class="popup-content">
        <div class="result-section">
          <div class="result-row">
            <span class="label">号码：</span>
            <span class="value">{{ afterGameQueryResult.fullBibno }}</span>
          </div>
          <div class="result-row">
            <span class="label">净成绩：</span>
            <span class="value">{{
              formatDuration(afterGameQueryResult.pureDuration)
            }}</span>
          </div>
          <div class="result-row">
            <span class="label">枪成绩：</span>
            <span class="value">{{
              formatDuration(afterGameQueryResult.gunDuration)
            }}</span>
          </div>
          <div class="result-row">
            <span class="label">平均配速：</span>
            <span class="value">{{
              formatPace(afterGameQueryResult.averagePace)
            }}</span>
          </div>
        </div>

        <div class="ranking-section">
          <h3>排名信息</h3>
          <div class="ranking-grid">
            <div class="ranking-item">
              <span class="ranking-label">净项目排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.pureProjectRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">枪项目排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.gunProjectRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">净性别排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.pureSexRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">枪性别排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.gunSexRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">净年龄排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.pureAgeRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">枪年龄排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.gunAgeRank
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">项目人数</span>
              <span class="ranking-value">{{
                afterGameQueryResult.projectCount
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">性别人数</span>
              <span class="ranking-value">{{
                afterGameQueryResult.sexCount
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">年龄组</span>
              <span class="ranking-value">{{
                afterGameQueryResult.ageRange
              }}</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-label">年龄组人数</span>
              <span class="ranking-value">{{
                afterGameQueryResult.ageCount
              }}</span>
            </div>
            <div class="ranking-item" v-if="afterGameQueryResult.teamName">
              <span class="ranking-label">队伍名称</span>
              <span class="ranking-value">{{
                afterGameQueryResult.teamName
              }}</span>
            </div>
            <div class="ranking-item" v-if="afterGameQueryResult.teamDuration !== 0">
              <span class="ranking-label">队伍用时</span>
              <span class="ranking-value">{{
                formatDuration(afterGameQueryResult.teamDuration)
              }}</span>
            </div>
            <div class="ranking-item" v-if="afterGameQueryResult.teamRank !== 0">
              <span class="ranking-label">队伍排名</span>
              <span class="ranking-value">{{
                afterGameQueryResult.teamRank
              }}</span>
            </div>
            <div class="ranking-item" v-if="afterGameQueryResult.teamMemberList">
              <span class="ranking-label">队伍成员</span>
              <span class="ranking-value">{{
               afterGameQueryResult.teamMemberList
              }}</span>
            </div>
          </div>
        </div>
        <van-collapse v-model="activeCollapseAfter" accordion>
          <van-collapse-item title="净时分段成绩" name="1">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in afterGameQueryResult.purePartTimeDurationList"
                :key="'net-' + key"
                class="split-item"
              >
                <span class="split-distance">计时点：{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>
              </div>
            </div>
          </van-collapse-item>

          <van-collapse-item title="枪时分段成绩" name="2">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in afterGameQueryResult.gunPartTimeDurationList"
                :key="'gun-' + key"
                class="split-item"
              >
                <span class="split-distance">计时点：{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>
              </div>
            </div>
          </van-collapse-item>

          <van-collapse-item title="净时累计成绩" name="3">
            <div class="split-times">
              <div
                v-for="(
                  time, key
                ) in afterGameQueryResult.pureAddTimeDurationList"
                :key="'cum-' + key"
                class="split-item"
              >
                <span class="split-distance">计时点：{{ key }}</span>
                <span class="split-time">{{ formatDuration(time) }}</span>  
              </div>
            </div>
          </van-collapse-item>
        </van-collapse>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from "vant";

export default {
  data() {
    return {
      //赛事加密id
      raceId: "",

      // 实时数据查询相关
      realTimeQueryNumber: "",
      realTimeLoading: false,
      showRealTimePopup: false,
      activeCollapse: "1",

      // 赛后数据查询相关
      afterGameQueryNumber: "",
      afterGameLoading: false,
      showAfterGamePopup: false,
      activeCollapseAfter: "1",

      // 统计数据
      data: {
        name: "",
        realTimeCount: 0,
        afterGamePushCount: 0,
        realTimeDistanceCountMap: {},
        afterGameDistanceCountMap: {},
      },

      // 查询结果数据
      realTimeQueryResult: {
        fullBibno: "",
        pureDuration: 0,
        gunDuration: 0,
        averagePace: 0,
        purePartTimeDurationList: {},
        gunPartTimeDurationList: {},
        pureAddTimeDurationList: {},
      },

      afterGameQueryResult: {
        fullBibno: "",
        pureDuration: 0,
        gunDuration: 0,
        averagePace: 0,
        purePartTimeDurationList: {},
        gunPartTimeDurationList: {},
        pureAddTimeDurationList: {},
        pureProjectRank: 0,
        pureSexRank: 0,
        ageRange: "",
        pureAgeRank: 0,
        gunProjectRank: 0,
        gunSexRank: 0,
        gunAgeRank: 0,
        projectCount: 0,
        sexCount: 0,
        ageCount: 0,
      },

      // 距离标签映射
      distanceLabels: {
        1: "5km",
        2: "10km",
        3: "15km",
        4: "20km",
        5: "21.0975km",
        6: "25km",
        7: "30km",
        8: "35km",
        9: "40km",
        10: "FINISH",
      },
    };
  },
  mounted() {
    const raceId = this.$route.query.race_id;
    if (raceId) {
      this.raceId = raceId;
      this.fetchData();
    } else {
      this.$router.push({
        path: `/errorPage`,
      });
    }
  },
  methods: {
    async fetchData() {
      try {
        const res = await this.$http.get(
          `/offline/user/result/query/${this.raceId}`
        );

        if (res.code !== 200) {
          Toast.fail(res.msg);
        } else {
          this.data = res.data;
        }
      } catch (error) {
        Toast.fail("网络错误，请稍后重试");
      }
    },

    formatDistance(meters) {
      // 将米转换为公里，保留1位小数
      const km = (parseFloat(meters) / 1000).toFixed(4);
      return `${km}公里`;
    },

    formatNumber(num) {
      // 格式化数字，添加千位分隔符
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // 将秒数格式化为 HH:mm:ss
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return "--:--:--";

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      return [
        hours.toString().padStart(2, "0"),
        minutes.toString().padStart(2, "0"),
        secs.toString().padStart(2, "0"),
      ].join(":");
    },

    // 将配速秒数格式化为 mm:ss
    formatPace(seconds) {
      if (!seconds && seconds !== 0) return "--:--";

      // 向下取整，确保没有小数位
      const totalSeconds = Math.floor(seconds);
      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;

      return [
        minutes.toString().padStart(2, "0"),
        secs.toString().padStart(2, "0"),
      ].join(":");
    },

    // 获取距离标签
    getDistanceLabel(key) {
      return this.distanceLabels[key] || key;
    },

    async handleRealTimeQuery() {
      if (!this.realTimeQueryNumber) {
        Toast.fail("请输入参赛号码");
        return;
      }

      this.realTimeLoading = true;

      const data = {
        raceId: this.raceId,
        fullBibno: this.realTimeQueryNumber,
      };

      try {
        const res = await this.$http({
          url: "/offline/user/result/real/time/detail",
          method: "get",
          params: data,
        });

        if (res.code !== 200) {
          Toast.fail(res.msg);
        } else {
          this.realTimeQueryResult = res.data;
          this.showRealTimePopup = true;
          Toast.success("实时成绩查询成功");
        }
      } catch (error) {
        Toast.fail("网络错误，请稍后重试");
      } finally {
        this.realTimeLoading = false;
      }
    },

    async handleAfterGameQuery() {
      if (!this.afterGameQueryNumber) {
        Toast.fail("请输入参赛号码");
        return;
      }

      this.afterGameLoading = true;

      const data = {
        raceId: this.raceId,
        fullBibno: this.afterGameQueryNumber,
      };
      try {
        const res = await this.$http({
          url: "/offline/user/result/after/game/detail",
          method: "get",
          params: data,
        });

        if (res.code !== 200) {
          Toast.fail(res.msg);
        } else {
          this.afterGameQueryResult = res.data;
          this.showAfterGamePopup = true;
          Toast.success("最终成绩查询成功");
        }
      } catch (error) {
        Toast.fail("网络错误，请稍后重试");
      } finally {
        this.afterGameLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.competition-container {
  padding: 16px;
  background: linear-gradient(to bottom, #f5f7fa, #e4efe9);
  min-height: 100vh;
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin: 20px 0 30px;
}

.page-header h1 {
  font-size: 24px;
  font-weight: bold;
  color: #34495e;
  margin: 0;
  padding: 10px 0;
  position: relative;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.page-header h1::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #3498db, #2ecc71);
  border-radius: 3px;
}

.stats-sections {
  margin-bottom: 30px;
}

.stats-card {
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 15px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.card-icon {
  margin-right: 8px;
  color: #3498db;
  font-size: 20px;
}

.total-count {
  margin-left: auto;
  font-size: 14px;
  color: #7f8c8d;
  font-weight: normal;
}

.stats-content {
  padding: 5px 0;
  margin-bottom: 15px;
}

.distance-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.distance-item:last-child {
  border-bottom: none;
}

.distance {
  font-weight: 500;
  color: #34495e;
}

.count {
  font-weight: bold;
  color: #27ae60;
}

.query-section {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed #eee;
}

.query-btn-container {
  padding: 0 16px;
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.query-btn {
  border-radius: 8px;
  background: linear-gradient(to right, #3498db, #2ecc71);
  border: none;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(52, 152, 219, 0.3);
}

/* 弹出层样式 */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 15px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.popup-header h2 {
  margin: 0;
  font-size: 18px;
  color: #34495e;
}

.close-icon {
  font-size: 20px;
  color: #969799;
  cursor: pointer;
}

.popup-content {
  padding: 0 20px 30px;
  overflow-y: auto;
  max-height: calc(100% - 60px);
}

.result-section {
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.result-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.result-row .label {
  font-weight: 500;
  color: #34495e;
}

.result-row .value {
  font-weight: bold;
  color: #27ae60;
}

.split-times {
  padding: 10px 0;
}

.split-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.split-item:last-child {
  border-bottom: none;
}

.split-distance {
  font-weight: 500;
  color: #34495e;
}

.split-time {
  font-weight: bold;
  color: #3498db;
}

.ranking-section {
  margin-top: 20px;
}

.ranking-section h3 {
  margin: 0 0 15px;
  font-size: 16px;
  color: #34495e;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.ranking-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.ranking-item {
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.ranking-label {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.ranking-value {
  font-size: 16px;
  font-weight: bold;
  color: #27ae60;
}

/* 响应式设计 */
@media (max-width: 320px) {
  .page-header h1 {
    font-size: 20px;
  }

  .card-title {
    font-size: 16px;
  }

  .total-count {
    font-size: 12px;
    margin-left: 0;
    width: 100%;
    margin-top: 5px;
  }

  .ranking-grid {
    grid-template-columns: 1fr;
  }
}
</style>
